import React, { useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { StyleSheet, View, ViewProps } from 'react-native'
import { string, object, number } from 'yup'
import { Color, Spacing, Text } from 'zens-storybook'
import { Draft, ScheduleData, ScheduleMode, ScheduleWeekdays, useSchedule, ScheduleWeekday, useFloor } from 'zens-store'
import { observer } from 'mobx-react-lite'
import { BuildingSelect, Form, FormProps, NavigationConfirm, ZoneSelect } from '@dms/component'
import { FloorSelect } from 'zens-fragment'

import ScheduleModeDescription from './mode'
// import ScheduleModeRadioGroup from './mode'
import ScheduleSpaceRadioGroup, { ScheduleSpace } from './space'
import ScheduleInput from './input'

const styles = StyleSheet.create({
  modeInput: {
    flex: 1,
    maxWidth: '100%',
  },
  space: {
    zIndex: 600,
  },
  building: {
    zIndex: 500,
  },
  floor: {
    zIndex: 400,
  },
  zone: {
    zIndex: 300,
  },
  schedule: {
    zIndex: 200,
  },
  label: {
    color: Color.metalGrey,
    marginTop: -Spacing.XS,
    marginBottom: Spacing.S,
  },
  spaceDescription: {
    color: Color.metalGrey,
    marginTop: Spacing.XS,
    marginBottom: Spacing.S,
  },
  skeletonSchedule: {
    maxWidth: '100%',
  },
  skeletonLabel: {
    width: 200,
    maxHeight: 16,
  },
})

interface DraftData extends ScheduleData {
  mode?: ScheduleMode
  space?: ScheduleSpace
}

interface Props extends Omit<FormProps, 'draft' | 'config' | 'schema' | 'buttons'> {
  companyId: number
  scheduleId?: number
  onSave?: (draft: Draft<DraftData>) => Promise<void>
  onDelete?: (draft: Draft<DraftData>) => Promise<void>
}
const ScheduleForm = (props: Props) => {
  const { companyId, scheduleId = 0, onSave, onDelete, ...otherProps } = props
  const { t } = useTranslation()
  const schedule = useSchedule({ companyId, scheduleId })

  const program = ScheduleWeekdays.map((day) => ({
    weekday: day as ScheduleWeekday,
    is_enabled: false,
    start_time: '09:00',
    end_time: '17:00',
    ...schedule.data?.program?.find((item) => item.weekday === day),
  })).sort((a, b) => ScheduleWeekdays.indexOf(a.weekday) - ScheduleWeekdays.indexOf(b.weekday))
  const space = schedule.data?.zoneId
    ? 'zone'
    : schedule.data?.floorId
      ? 'floor'
      : schedule.data?.buildingId
        ? 'building'
        : undefined
  const draft = useMemo(
    () =>
      new Draft<DraftData>({
        id: 0,
        company_id: companyId,
        mode: ScheduleMode.Clean,
        space,
        ...schedule.data?.data,
        program,
      }),
    [schedule.data],
  )
  const buildingId = draft.data.building_id
  const floorId = draft.data.floor_id || NaN

  const floor = useFloor({
    companyId,
    floorId,
  })

  useEffect(() => {
    if (floor.data?.buildingId && floor.data?.buildingId !== buildingId) {
      draft.update('building_id', floor.data?.buildingId)
      draft.settle('building_id')
    }
  }, [floor.data?.buildingId])

  useEffect(() => {
    if (draft.data?.space === 'building') {
      draft.remove('floor_id')
      draft.remove('zone_id')
    } else if (draft.data?.space === 'floor') {
      draft.remove('zone_id')
    } else if (draft.data?.space === 'zone') {
      draft.remove('building_id')
      draft.remove('floor_id')
    }
  }, [draft.data?.space])

  const config = {
    groups: [
      {
        label: t('Cleaning mode'),
        fields: [
          {
            key: 'mode',
            FieldInputComponent: ScheduleModeDescription,
            fieldProps: {
              style: styles.modeInput,
            },
          },
          {
            key: 'name',
            label: t('Name'),
          },
          {
            label: (
              <View>
                <Text level={5}>{t('Select scheduling space')}</Text>
                <Text style={styles.spaceDescription}>{t('Select for which workspaces you want to schedule')}</Text>
              </View>
            ),
            key: 'space',
            FieldInputComponent: ScheduleSpaceRadioGroup,
          },
          ...(draft.data.space === 'building' || draft.data.space === 'floor'
            ? [
                {
                  key: 'building_id',
                  label: t('Building'),
                  FieldInputComponent: BuildingSelect,
                  fieldProps: {
                    companyId,
                    groupLocation: true,
                  },
                  style: styles.building,
                },
              ]
            : []),
          ...(draft.data.space === 'floor'
            ? [
                {
                  key: 'floor_id',
                  label: t('Floor'),
                  FieldInputComponent: FloorSelect,
                  fieldProps: {
                    companyId,
                    buildingId,
                  },
                  style: styles.floor,
                },
              ]
            : []),
          ...(draft.data.space === 'zone'
            ? [
                {
                  key: 'zone_id',
                  label: t('Zone'),
                  FieldInputComponent: ZoneSelect,
                  fieldProps: {
                    companyId,
                  },
                  style: styles.zone,
                },
              ]
            : []),
        ],
        style: styles.space,
      },
      {
        label: t('Time schedule'),
        fields: [
          {
            label: t('Use the slider to select the time slots on which your schedule is active.'),
            labelStyle: styles.label,
            key: 'program',
            FieldInputComponent: ScheduleInput,
            fieldProps: {
              color: Color.lightGreyBlue,
              companyId,
              buildingId,
            },
          },
        ],
        style: styles.schedule,
      },
    ],
  }
  const schema = object({
    name: string().required(t('Name is required')),
    space: string().required(t('Scheduling space is required')),
    ...(draft.data.space === 'building' || draft.data.space === 'floor'
      ? { building_id: number().required(t('Building is required')) }
      : {}),
    ...(draft.data.space === 'floor' ? { floor_id: number().required(t('Floor is required')) } : {}),
    ...(draft.data.space === 'zone'
      ? {
          zone_id: number().required(t('Zone is required')),
        }
      : {}),
  })
  const buttons = [
    [
      {
        title: t('Save'),
        onPress: onSave,
        validate: true,
      },
      {
        title: t('Cancel'),
        href: `/companies/${companyId}/workspaces/schedules`,
        light: true,
      },
    ],
    ...(onDelete
      ? [
          {
            title: t('Delete schedule'),
            onPress: onDelete,
            left: 'trash' as const,
            danger: true,
          },
        ]
      : []),
  ]
  return (
    <>
      <Form draft={draft} config={config} schema={schema} buttons={buttons} {...otherProps} />
      <NavigationConfirm draft={draft} />
    </>
  )
}

interface SkeletonProps extends ViewProps {}

const Skeleton = (props: SkeletonProps) => {
  const { t } = useTranslation()

  const config = {
    groups: [
      {
        label: t('Cleaning mode'),
        fields: [
          {
            key: 'mode',
            FieldInputComponent: ScheduleModeDescription.Skeleton,
            fieldProps: {
              style: styles.modeInput,
            },
          },
          {
            key: 'name',
            label: t('Name'),
          },
          // {
          //   key: 'mode',
          //   label: t('Mode'),
          //   FieldInputComponent: ScheduleModeRadioGroup.Skeleton,
          //   fieldProps: {
          //     style: styles.modeInput,
          //   },
          // },
          {
            label: (
              <View>
                <Text level={5}>{t('Select scheduling space')}</Text>
                <Text style={styles.spaceDescription}>{t('Select for which workspaces you want to schedule')}</Text>
              </View>
            ),
            key: 'space',
          },
          {
            key: 'building_id',
            label: <Text.Skeleton style={styles.skeletonLabel} />,
            style: styles.building,
          },
        ],
        style: styles.space,
      },
      {
        label: t('Time schedule'),
        fields: [
          {
            label: t('Use the slider to select the time slots on which your schedule is active.'),
            labelStyle: styles.label,
            key: 'program',
            FieldInputComponent: ScheduleInput.Skeleton,
            inputStyle: styles.skeletonSchedule,
          },
        ],
        style: styles.schedule,
      },
    ],
  }
  return <Form.Skeleton config={config} numberOfbuttons={[2, 1]} {...props} />
}

ScheduleForm.Skeleton = Skeleton

export default observer(ScheduleForm)
